import { useAuth0 } from "@auth0/auth0-react";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGlobalLoading } from "../components/loading/global-loading-context";
import { useMobileAuth } from "../contexts/mobile-auth-context";
import { useAnalytics } from "../lib/analytics";
import { performLogout } from "../lib/auth-logout";

export function Login() {
	const {
		loginWithRedirect,
		isAuthenticated,
		isLoading,
		getAccessTokenSilently,
		logout,
	} = useAuth0();
	const { setLoading } = useGlobalLoading();
	const { isMobileAuthReady } = useMobileAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [loginError, setLoginError] = useState<string | null>(null);
	const [isAuthReady, setIsAuthReady] = useState(false);
	// Used to force re-render when browser closes (refs don't trigger re-renders)
	const [, setLoginResetKey] = useState(0);
	const loginAttemptInProgress = useRef(false);
	const hasAttemptedLogin = useRef(false);
	const analytics = useAnalytics();

	// Check if this is a signup flow (from invitation)
	const isSignupFlow = searchParams.get("signup") === "true";
	const isNative = Capacitor.isNativePlatform();

	// Track page view
	useEffect(() => {
		analytics.logEvent("page_view", "login", {
			is_signup_flow: String(isSignupFlow),
			platform: Capacitor.getPlatform(),
			is_native: String(isNative),
		});
	}, [analytics, isSignupFlow, isNative]);

	// Reset login attempt refs on component mount
	// This allows retry if user returns to login page after an error
	useEffect(() => {
		console.log("🔄 Resetting login attempt refs on mount");
		loginAttemptInProgress.current = false;
		hasAttemptedLogin.current = false;
	}, []);

	// Listen for browser close events (user clicked "Done" to cancel login)
	// This resets the login state so the button becomes enabled again
	useEffect(() => {
		// Only needed on native platforms where in-app browser is used
		if (!isNative) return;

		const setupListener = async () => {
			const listener = await Browser.addListener("browserFinished", () => {
				console.log("📱 In-app browser closed by user");

				// Reset login state to allow retry after a small delay
				// The delay avoids race condition with successful auth callback
				// (which also closes the browser but navigates away)
				setTimeout(() => {
					// Only reset if login was in progress and user is not authenticated
					if (loginAttemptInProgress.current && !isAuthenticated) {
						console.log("🔄 Resetting login state after browser close");
						loginAttemptInProgress.current = false;
						hasAttemptedLogin.current = false;

						// Force re-render since refs don't trigger updates
						setLoginResetKey((k) => k + 1);
					}
				}, 500);
			});

			return listener;
		};

		let listenerCleanup: { remove: () => void } | undefined;
		setupListener().then((listener) => {
			listenerCleanup = listener;
		});

		return () => {
			if (listenerCleanup) {
				listenerCleanup.remove();
			}
		};
	}, [isNative, isAuthenticated]);

	// Wait for Auth0 SDK to fully settle before allowing login
	// On mobile after logout, SDK needs time to clear internal state
	useEffect(() => {
		if (!isLoading) {
			// Longer delay on mobile to ensure SDK is ready after logout
			const delay = isNative ? 1000 : 100;
			const timer = setTimeout(() => {
				console.log("🔓 Auth0 SDK ready for login");
				setIsAuthReady(true);
			}, delay);
			return () => clearTimeout(timer);
		} else {
			// Reset when loading starts
			setIsAuthReady(false);
		}
	}, [isLoading, isNative]);

	const handleLogin = useCallback(
		async (forceSignup: boolean = false) => {
			// Prevent multiple simultaneous login attempts
			if (loginAttemptInProgress.current) {
				console.log("⚠️ Login attempt already in progress, skipping...");
				return;
			}

			loginAttemptInProgress.current = true;
			hasAttemptedLogin.current = true;
			setLoginError(null);

			const isNative = Capacitor.isNativePlatform();
			const shouldShowSignup = forceSignup || isSignupFlow;

			// Track login initiated
			analytics.logUserAction("login_initiated", {
				is_signup: String(shouldShowSignup),
				platform: Capacitor.getPlatform(),
				is_native: String(isNative),
				trigger: forceSignup ? "manual_button" : "auto",
			});

			try {
				console.log(
					`Starting Auth0 ${shouldShowSignup ? "signup" : "login"} process (${isNative ? "mobile" : "web"})...`,
				);

				// Build auth parameters
				const authParams = {
					appState: { returnTo: "/callback" },
					...(shouldShowSignup && {
						authorizationParams: {
							screen_hint: "signup",
						},
					}),
				};

				if (isNative) {
					// Mobile: Use Capacitor Browser plugin for optimal UX
					console.log("🚀 Mobile login: Using Capacitor browser plugin");
					console.log(
						"🚀 Mobile login: Capacitor.isNativePlatform():",
						Capacitor.isNativePlatform(),
					);
					console.log(
						"🚀 Mobile login: Capacitor.getPlatform():",
						Capacitor.getPlatform(),
					);
					await loginWithRedirect({
						...authParams,
						async openUrl(url) {
							console.log("🚀 Mobile login: openUrl callback triggered");
							console.log("🚀 Mobile login: Opening browser with URL:", url);
							console.log("🚀 Mobile login: About to call Browser.open()");
							try {
								// Redirect using Capacitor's Browser plugin
								// This opens SFSafariViewController on iOS, Chrome Custom Tabs on Android
								await Browser.open({
									url,
									windowName: "_self",
								});
								console.log(
									"✅ Mobile login: Browser.open() completed successfully",
								);
							} catch (error) {
								console.error("❌ Mobile login: Browser.open() failed:", error);
								throw error; // Re-throw to be caught by outer catch
							}
						},
					});
					console.log("🚀 Mobile login: loginWithRedirect() completed");
				} else {
					// Web: Use standard redirect
					console.log("🌐 Web login: Using standard redirect");
					await loginWithRedirect(authParams);
				}
			} catch (error) {
				console.error("❌ Login failed:", error);
				const errorMessage =
					error instanceof Error
						? error.message
						: "Login failed. Please try again.";
				setLoginError(errorMessage);

				// Track login error
				analytics.logError(errorMessage, {
					context: "login_attempt",
					is_signup: String(shouldShowSignup),
					platform: Capacitor.getPlatform(),
				});

				loginAttemptInProgress.current = false; // Allow retry on error
			}
		},
		[loginWithRedirect, isSignupFlow, analytics],
	);

	// Auto-redirect authenticated users to callback for claims processing
	// But first validate the token is actually usable (not expired)
	useEffect(() => {
		if (!isLoading && isAuthenticated) {
			const validateAndRedirect = async () => {
				try {
					// Attempt to get a fresh token - this will use refresh token if access token is expired
					console.log("🔐 User appears authenticated, validating token...");
					await getAccessTokenSilently({
						authorizationParams: {
							audience: import.meta.env.VITE_AUTH0_AUDIENCE,
						},
					});
					// Token is valid, safe to redirect
					console.log("✅ Token validated, redirecting to callback");
					analytics.logUserAction("login_auto_redirect", {
						reason: "returning_user",
						platform: Capacitor.getPlatform(),
					});
					navigate("/callback");
				} catch (error) {
					// Token refresh failed - clear stale auth state and let user re-login
					console.error(
						"❌ Token validation failed, clearing stale auth state:",
						error,
					);
					analytics.logError("token_validation_failed", {
						context: "login_redirect",
						error: error instanceof Error ? error.message : "Unknown error",
					});

					// Use performLogout for consistent cleanup (includes deduplication)
					// This properly clears Auth0 SDK internal state and prevents login loops
					await performLogout(logout);

					// Wait for Auth0 SDK to fully update internal state
					// This prevents the auto-login from firing before SDK is ready
					await new Promise((resolve) => setTimeout(resolve, 1000));

					// Reset refs to allow fresh login attempt
					loginAttemptInProgress.current = false;
					hasAttemptedLogin.current = false;
				}
			};

			validateAndRedirect();
		}
	}, [
		isAuthenticated,
		isLoading,
		navigate,
		getAccessTokenSilently,
		logout,
		analytics,
	]);

	// Auto-trigger login for unauthenticated users
	useEffect(() => {
		// Wait for:
		// 1. Auth0 to finish loading
		// 2. User to not be authenticated
		// 3. Mobile auth infrastructure to be ready (appUrlOpen listener registered)
		// 4. Auth0 SDK to be fully ready (after settling from logout)
		//    On web, isMobileAuthReady is always true immediately
		if (!isLoading && !isAuthenticated && isMobileAuthReady && isAuthReady) {
			// On mobile, add a small delay to allow any final Auth0 state cleanup
			// This prevents "Unable to display URL" errors when redirecting too quickly
			const delay = isNative ? 500 : 0;

			const timer = setTimeout(() => {
				console.log("✅ All checks passed, auto-triggering Auth0 login...");
				handleLogin();
			}, delay);

			return () => clearTimeout(timer);
		}
	}, [
		isLoading,
		isAuthenticated,
		isMobileAuthReady,
		isAuthReady,
		isNative,
		handleLogin,
	]);

	useEffect(() => {
		setLoading(isLoading);
	}, [isLoading, setLoading]);

	if (isLoading) {
		return null; // Global loader will show
	}

	// Show minimal UI while redirecting to Auth0 (should be very brief)
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				height: "100vh",
				fontFamily: "'Urbanist', system-ui, sans-serif",
				backgroundColor: "#ffffff",
			}}
		>
			<div
				style={{
					textAlign: "center",
				}}
			>
				<img
					src="/images/era-text-black.png"
					alt="Era"
					style={{
						height: "40px",
						marginBottom: "1rem",
					}}
				/>
				<p style={{ color: "#666", marginBottom: "1.5rem" }}>
					Redirecting to sign in...
				</p>

				{/* Manual fallback button if auto-redirect fails */}
				<button
					type="button"
					onClick={() => handleLogin()}
					disabled={loginAttemptInProgress.current}
					style={{
						padding: "12px 32px",
						fontSize: "16px",
						fontFamily: "'Urbanist', system-ui, sans-serif",
						fontWeight: 600,
						color: "#ffffff",
						backgroundColor: "#000000",
						border: "none",
						borderRadius: "8px",
						cursor: loginAttemptInProgress.current ? "not-allowed" : "pointer",
						opacity: loginAttemptInProgress.current ? 0.6 : 1,
						transition: "opacity 0.2s",
					}}
					onMouseEnter={(e) => {
						if (!loginAttemptInProgress.current) {
							e.currentTarget.style.opacity = "0.8";
						}
					}}
					onMouseLeave={(e) => {
						if (!loginAttemptInProgress.current) {
							e.currentTarget.style.opacity = "1";
						}
					}}
				>
					{loginAttemptInProgress.current ? "Signing in..." : "Sign In"}
				</button>

				{loginError && (
					<p style={{ color: "#ef4444", marginTop: "1rem", fontSize: "14px" }}>
						{loginError}
					</p>
				)}
			</div>
		</div>
	);
}
